import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Inventory2Outlined as DevicesIcon,
  CheckCircleOutlined as OnlineIcon,
  ErrorOutlineOutlined as OfflineIcon,
  VerifiedUser as UnverifiedIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAppSelector } from '@hooks/redux';
import MetricCard from '@components/dashboard/MetricCard';
import {
  useGetDashboardMetricsQuery,
} from '@store/api/dashboardApi';
import { DashboardErrorBoundary } from '@components/dashboard/DashboardErrorBoundary';
import DashboardPreferences from '@components/dashboard/DashboardPreferences';
import { useDashboardPreferences } from '@hooks/useDashboardPreferences';

const DashboardPage: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const { preferences, updatePreferences } = useDashboardPreferences();
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);

  const {
    data: metrics,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
  } = useGetDashboardMetricsQuery();

  // Auto-refresh based on preferences
  useEffect(() => {
    const intervalMs = preferences.layout.refreshInterval * 1000;

    const interval = setInterval(() => {
      refetchMetrics();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [preferences.layout.refreshInterval, refetchMetrics]);

  const metricCards = [
    {
      title: 'Total Devices',
      value: metrics?.total_devices || 0,
      icon: <DevicesIcon sx={{ fontSize: 32 }} />,
      color: 'primary' as const,
    },
    {
      title: 'Online Devices',
      value: metrics?.online_devices || 0,
      icon: <OnlineIcon sx={{ fontSize: 32 }} />,
      color: 'success' as const,
    },
    {
      title: 'Offline Devices',
      value: metrics?.offline_devices || 0,
      icon: <OfflineIcon sx={{ fontSize: 32 }} />,
      color: 'error' as const,
    },
    {
      title: 'Unverified Devices',
      value: metrics?.unverified_devices || 0,
      icon: <UnverifiedIcon sx={{ fontSize: 32 }} />,
      color: 'warning' as const,
    },
  ];

  return (
    <DashboardErrorBoundary>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Welcome back, {user?.full_name || user?.username || 'User'}!
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Dashboard Settings">
              <IconButton onClick={() => setPreferencesOpen(true)}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={3}>
        {metricCards.map((metric, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <MetricCard
              {...metric}
              loading={metricsLoading}
            />
          </Grid>
        ))}

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader
              title="Disk Capacity Distribution"
              slotProps={{
                title: {
                  variant: 'h6',
                  fontWeight: 600,
                }
              }}
            />
            <CardContent>
              {metrics?.disk_capacity_stats && metrics.disk_capacity_stats.length > 0 ? (
                <Box>
                  {metrics.disk_capacity_stats.map((stat, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500
                        }}>
                          {stat.category}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500
                          }}>
                          {stat.count} devices • {stat.percentage}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 10,
                          bgcolor: 'grey.100',
                          borderRadius: 2,
                          overflow: 'hidden',
                          position: 'relative',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${stat.percentage}%`,
                            height: '100%',
                            background: index === 0
                              ? 'linear-gradient(90deg, #EF4444, #DC2626)'
                              : index === 1
                              ? 'linear-gradient(90deg, #F59E0B, #D97706)'
                              : index === 2
                              ? 'linear-gradient(90deg, #3B82F6, #2563EB)'
                              : 'linear-gradient(90deg, #10B981, #059669)',
                            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRadius: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary'
                  }}>
                    No disk capacity data available
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500
                  }}>
                  Last updated: {metrics?.last_updated ? new Date(metrics.last_updated).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      <DashboardPreferences
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        preferences={preferences}
        onPreferencesChange={updatePreferences}
      />
    </DashboardErrorBoundary>
  );
};

export default DashboardPage;
